import React, { useState } from "react";
import PropTypes from "prop-types";
import { useAuth0 } from "@auth0/auth0-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faUserPlus,
  faUserCheck,
} from "@fortawesome/free-solid-svg-icons";
import { useFollowStatus } from "../../hooks/user/useFollowStatus";
import { useDebouncedFollowAction } from "../../hooks/user/useDebouncedFollowAction";

/**
 * FollowButton component with three states (Follow, Following, Loading)
 *
 * Implements optimistic updates, hover effects, responsive sizing, and error handling.
 * Integrates with follow/unfollow hooks for complete follow functionality.
 *
 * Requirements: 1.1, 1.2, 2.1, 2.2, 7.1, 7.2, 7.3, 7.4, 7.6, 8.1, 8.2
 */
const FollowButton = ({
  userId,
  initialFollowStatus = false,
  size = "md",
  variant = "primary",
  className = "",
  disabled = false,
  showIcon = true,
  ...rest
}) => {
  const { user } = useAuth0();
  const [isHovered, setIsHovered] = useState(false);

  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  // Hooks for follow operations
  const {
    toggleFollow,
    isLoading: actionLoading,
    cleanup,
  } = useDebouncedFollowAction(300); // 300ms debounce

  // Get current follow status with fallback to initial status
  const {
    data: followStatusData,
    isLoading: statusLoading,
    error: statusError,
    refetch: refetchStatus,
  } = useFollowStatus(userId, {
    initialData: initialFollowStatus
      ? { isFollowing: true }
      : { isFollowing: false },
  });

  // Cleanup on unmount
  React.useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  // NOW we can do early returns after all hooks are called
  if (userId === user?.sub) {
    return null;
  }

  // Determine current state - handle nested data structure from backend
  const isFollowing =
    followStatusData?.data?.isFollowing ||
    followStatusData?.isFollowing ||
    false;
  const isLoading = actionLoading || statusLoading;
  const hasError = statusError && !followStatusData;

  // Handle follow/unfollow actions
  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled || isLoading) return;

    // If there's an error state and button shows "Retry", refetch status
    if (hasError && buttonContent.onClick) {
      buttonContent.onClick();
      return;
    }

    // Use debounced toggle action
    toggleFollow(userId, isFollowing);
  };

  // Size-specific styles
  const sizeStyles = {
    xs: "text-xs px-2 py-1 min-w-[60px]",
    sm: "text-xs px-3 py-1.5 min-w-[70px]",
    md: "text-sm px-4 py-2 min-w-[80px]",
    lg: "text-base px-5 py-2.5 min-w-[90px]",
  };

  // Icon sizes based on button size
  const iconSizes = {
    xs: "text-xs",
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base",
  };

  // Determine button text and styling based on state
  const getButtonContent = () => {
    if (isLoading) {
      return {
        text: isFollowing ? "Unfollowing..." : "Following...",
        icon: faSpinner,
        iconClass: "animate-spin",
        bgClass: "bg-gray-400 cursor-not-allowed",
        hoverClass: "",
        textClass: "text-white",
      };
    }

    if (hasError) {
      return {
        text: "Retry",
        icon: faUserPlus,
        iconClass: "",
        bgClass: "bg-gray-400",
        hoverClass: "hover:bg-gray-500",
        textClass: "text-white",
        onClick: () => refetchStatus(),
      };
    }

    if (isFollowing) {
      if (isHovered) {
        return {
          text: "Unfollow",
          icon: faUserCheck,
          iconClass: "",
          bgClass: "bg-red-500 border-red-500",
          hoverClass: "",
          textClass: "text-white",
        };
      }
      return {
        text: "Following",
        icon: faUserCheck,
        iconClass: "",
        bgClass: "bg-gray-500 border-gray-500",
        hoverClass: "hover:bg-red-500 hover:border-red-500",
        textClass: "text-white",
      };
    }

    // Not following state
    return {
      text: "Follow",
      icon: faUserPlus,
      iconClass: "",
      bgClass:
        variant === "primary"
          ? "bg-blue-500 border-blue-500"
          : "bg-deep-red border-deep-red",
      hoverClass:
        variant === "primary"
          ? "hover:bg-blue-600 hover:border-blue-600"
          : "hover:bg-bright-red hover:border-bright-red",
      textClass: "text-white",
    };
  };

  const buttonContent = getButtonContent();

  // Base button classes
  const baseClasses =
    "inline-flex items-center justify-center font-medium transition-all duration-200 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 border";

  // Disabled state
  const disabledClasses =
    disabled || isLoading ? "opacity-60 cursor-not-allowed" : "cursor-pointer";

  // Combine all classes
  const buttonClasses = `
    ${baseClasses}
    ${sizeStyles[size]}
    ${buttonContent.bgClass}
    ${buttonContent.hoverClass}
    ${buttonContent.textClass}
    ${disabledClasses}
    ${className}
  `
    .trim()
    .replace(/\s+/g, " ");

  return (
    <button
      type="button"
      className={buttonClasses}
      onClick={handleClick}
      disabled={disabled || isLoading}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label={isFollowing ? "Unfollow user" : "Follow user"}
      {...rest}
    >
      {showIcon && (
        <FontAwesomeIcon
          icon={buttonContent.icon}
          className={`${iconSizes[size]} ${buttonContent.iconClass} mr-2`}
        />
      )}
      <span className="whitespace-nowrap">{buttonContent.text}</span>
    </button>
  );
};

FollowButton.propTypes = {
  userId: PropTypes.string.isRequired,
  initialFollowStatus: PropTypes.bool,
  size: PropTypes.oneOf(["xs", "sm", "md", "lg"]),
  variant: PropTypes.oneOf(["primary", "secondary"]),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  showIcon: PropTypes.bool,
};

export default FollowButton;
